import { CookieConsentConfig } from "vanilla-cookieconsent";
import * as CookieConsent from "vanilla-cookieconsent";
import ReactGA from "react-ga4";

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";

const handleConsent = () => {
  // Use any cast to bypass the missing export member error in TS definitions
  const cc = CookieConsent as any;
  const cookie = cc.getCookie ? cc.getCookie() : null;

  if (cookie && cookie.categories && cookie.categories.includes('analytics')) {
    if (!ReactGA.isInitialized) {
      ReactGA.initialize(GA_MEASUREMENT_ID);
    }
    ReactGA.send({ hitType: "pageview", page: window.location.pathname });
  }
};

const pluginConfig: CookieConsentConfig = {
  guiOptions: {
    consentModal: {
      layout: "box",
      position: "middle center",
      equalWeightButtons: true,
      flipButtons: false,
    },
    preferencesModal: {
      layout: "box",
      position: "left",
      equalWeightButtons: true,
      flipButtons: false,
    },
  },

  onFirstConsent: () => {
    handleConsent();
  },

  onConsent: () => {
    handleConsent();
  },

  onChange: () => {
    handleConsent();
  },

  categories: {
    necessary: {
      readOnly: true,
      enabled: true,
    },
    analytics: {
      autoClear: {
        cookies: [
          {
            name: /^(_ga|_gid)/,
          },
        ],
      },
    },
  },

  language: {
    default: "de",
    translations: {
      de: {
        consentModal: {
          title: "Einwilligung zu Cookies & Daten",
          description:
              'Wir verwenden Cookies, um die Funktionalität von moonskins.gg zu gewährleisten und unsere Dienste durch Analysen zu verbessern. Durch Klicken auf "Alle akzeptieren" stimmen Sie der Verwendung dieser Dienste zu. Details finden Sie in unseren <a href="/privacy" data-cc="show-preferencesModal" class="cc__link">Cookie-Einstellungen</a>.',
          acceptAllBtn: "Alle akzeptieren",
          acceptNecessaryBtn: "Nur notwendige",
          showPreferencesBtn: "Einstellungen verwalten",
          footer: `
            <a href="/privacy">Datenschutzerklärung</a>
            <a href="/legal-disclosure">Impressum</a>
          `,
        },
        preferencesModal: {
          title: "Ihre Privatsphäre-Einstellungen",
          acceptAllBtn: "Alle akzeptieren",
          acceptNecessaryBtn: "Nur notwendige",
          savePreferencesBtn: "Einstellungen speichern",
          closeIconLabel: "Schließen",
          sections: [
            {
              title: "Verwendung von Cookies",
              description:
                  'Um Ihr Nutzererlebnis auf moonskins.gg zu optimieren, verwenden wir Cookies und vergleichbare Technologien. Sie können Ihre Präferenzen hier individuell anpassen.',
            },
            {
              title: "Technisch notwendige Cookies",
              description:
                  "Diese Cookies sind für den Betrieb der Website zwingend erforderlich, um grundlegende Funktionen wie Sicherheit, Anmeldung und Warenkorb-Funktionen zu ermöglichen. Sie können nicht deaktiviert werden.",
              linkedCategory: "necessary",
            },
            {
              title: "Analyse- und Performance-Cookies",
              description:
                  "Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren. Die Informationen werden anonymisiert erhoben und dienen der Verbesserung unserer Plattform.",
              linkedCategory: "analytics",
              cookieTable: {
                headers: {
                  name: "Name",
                  domain: "Dienst",
                  description: "Zweck",
                  expiration: "Ablauf",
                },
                body: [
                  {
                    name: "_ga",
                    domain: "Google Analytics",
                    description: "Unterscheidung der Nutzer zur statistischen Erfassung der Webseitenbesuche.",
                    expiration: "2 Jahre",
                  },
                  {
                    name: "_gid",
                    domain: "Google Analytics",
                    description: "Speicherung und Zählung der Seitenaufrufe während einer Sitzung.",
                    expiration: "24 Stunden",
                  },
                ],
              },
            },
            {
              title: "Weitere Informationen",
              description:
                  'Bei Fragen zu unserer Cookie-Richtlinie kontaktieren Sie uns bitte über unser Support-System oder lesen Sie die vollständige <a class="cc__link" href="/privacy">Datenschutzerklärung</a>.',
            },
          ],
        },
      },
      en: {
        consentModal: {
          title: "Cookie & Data Consent",
          description:
              'We use cookies to ensure the functionality of moonskins.gg and to improve our services through analytics. By clicking "Accept all", you consent to the use of these services. For details, see our <a href="/privacy" data-cc="show-preferencesModal" class="cc__link">cookie settings</a>.',
          acceptAllBtn: "Accept all",
          acceptNecessaryBtn: "Necessary only",
          showPreferencesBtn: "Manage preferences",
          footer: `
            <a href="/privacy">Privacy Policy</a>
            <a href="/legal-disclosure">Legal Disclosure</a>
          `,
        },
        preferencesModal: {
          title: "Privacy Preferences",
          acceptAllBtn: "Accept all",
          acceptNecessaryBtn: "Necessary only",
          savePreferencesBtn: "Save preferences",
          closeIconLabel: "Close",
          sections: [
            {
              title: "Cookie Usage",
              description:
                  'To optimize your experience on moonskins.gg, we use cookies and similar technologies. You can customize your preferences here.',
            },
            {
              title: "Strictly Necessary Cookies",
              description:
                  "These cookies are essential for the operation of the website to enable core functions such as security, network management, and shopping cart features. They cannot be disabled.",
              linkedCategory: "necessary",
            },
            {
              title: "Analytics & Performance Cookies",
              description:
                  "These cookies help us understand how visitors interact with our website. The information is collected anonymously and is used to improve our platform.",
              linkedCategory: "analytics",
              cookieTable: {
                headers: {
                  name: "Name",
                  domain: "Service",
                  description: "Purpose",
                  expiration: "Expiration",
                },
                body: [
                  {
                    name: "_ga",
                    domain: "Google Analytics",
                    description: "Distinguishes users for statistical recording of website visits.",
                    expiration: "2 years",
                  },
                  {
                    name: "_gid",
                    domain: "Google Analytics",
                    description: "Storage and counting of page views during a session.",
                    expiration: "24 hours",
                  },
                ],
              },
            },
            {
              title: "More information",
              description:
                  'For any queries in relation to my policy on cookies and your choices, please <a class="cc__link" href="/contact">contact us</a>.',
            },
          ],
        },
      },
    },
  },
};

export default pluginConfig;